// Add / edit an identity provider (a Dex upstream connector). Type is chosen on
// create and fixed on edit (it maps to the connector id). Conditional fields:
// tenantId for Microsoft 365, issuer for a generic OIDC provider. The client
// secret is write-only — on edit it stays blank and is only sent when retyped.
import React, { useState } from "react";
import { C, F, Btn } from "../brand/apo";
import {
  api,
  type IdpProvider,
  type IdpType,
  type CreateIdpProviderInput,
  type UpdateIdpProviderInput,
} from "../api";

const labelStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  color: C.fgFaint,
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: 7,
  display: "block",
};
const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: C.surfaceHi,
  border: `1px solid ${C.line}`,
  color: C.fgDark,
  fontFamily: F.ui,
  fontSize: 13.5,
  padding: "9px 12px",
  outline: "none",
};

const TYPE_OPTIONS: { id: IdpType; label: string }[] = [
  { id: "GOOGLE", label: "Google" },
  { id: "MICROSOFT", label: "Microsoft 365" },
  { id: "OIDC", label: "Generic OIDC" },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

export function IdpProviderForm({
  provider,
  redirectUri,
  onSaved,
  onCancel,
}: {
  provider?: IdpProvider | null;
  redirectUri: string | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const editing = !!provider;
  const [type, setType] = useState<IdpType>(provider?.type ?? "GOOGLE");
  const [displayName, setDisplayName] = useState(provider?.displayName ?? "");
  const [clientId, setClientId] = useState(provider?.clientId ?? "");
  const [clientSecret, setClientSecret] = useState("");
  const [tenantId, setTenantId] = useState(provider?.tenantId ?? "");
  const [issuer, setIssuer] = useState(provider?.issuer ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Required-field gate; the API is the authoritative validator. On edit the
  // secret may stay blank (keeps the stored one).
  const valid =
    displayName.trim() !== "" &&
    clientId.trim() !== "" &&
    (editing || clientSecret !== "") &&
    (type !== "MICROSOFT" || tenantId.trim() !== "") &&
    (type !== "OIDC" || issuer.trim() !== "");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !valid) return;
    setSubmitting(true);
    setError(null);
    try {
      if (editing && provider) {
        const body: UpdateIdpProviderInput = {
          displayName: displayName.trim(),
          clientId: clientId.trim(),
        };
        if (clientSecret) body.clientSecret = clientSecret;
        if (type === "MICROSOFT") body.tenantId = tenantId.trim();
        if (type === "OIDC") body.issuer = issuer.trim();
        await api.updateIdpProvider(provider.id, body);
      } else {
        const body: CreateIdpProviderInput = {
          type,
          displayName: displayName.trim(),
          clientId: clientId.trim(),
          clientSecret,
        };
        if (type === "MICROSOFT") body.tenantId = tenantId.trim();
        if (type === "OIDC") body.issuer = issuer.trim();
        await api.createIdpProvider(body);
      }
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <Field label="Provider type">
        <select
          style={{ ...inputStyle, appearance: "auto", opacity: editing ? 0.6 : 1 }}
          value={type}
          disabled={editing}
          onChange={(e) => setType(e.target.value as IdpType)}
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Display name">
        <input
          style={inputStyle}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={type === "GOOGLE" ? "Google" : type === "MICROSOFT" ? "Microsoft 365" : "Company SSO"}
          autoFocus
        />
      </Field>

      <Field label="Client ID">
        <input style={inputStyle} value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
      </Field>

      <Field label={editing ? "Client secret (leave blank to keep)" : "Client secret"}>
        <input
          style={inputStyle}
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          placeholder={editing && provider?.hasClientSecret ? "•••••••••• (unchanged)" : ""}
          autoComplete="off"
        />
      </Field>

      {type === "MICROSOFT" && (
        <Field label="Tenant ID">
          <input
            style={inputStyle}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="common, organizations, or a directory (tenant) ID"
            autoComplete="off"
          />
        </Field>
      )}

      {type === "OIDC" && (
        <Field label="Issuer URL">
          <input
            style={inputStyle}
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="https://idp.example.com"
            autoComplete="off"
          />
        </Field>
      )}

      {redirectUri && (
        <div style={{ marginTop: -2, marginBottom: 14, fontFamily: F.mono, fontSize: 10.5, color: C.fgFaint, lineHeight: 1.6 }}>
          Register this redirect URI at the provider console:
          <br />
          <span style={{ color: C.fgMute, wordBreak: "break-all" }}>{redirectUri}</span>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 4, marginBottom: 14, padding: "10px 12px", background: `${C.rose}14`, border: `1px solid ${C.rose}55`, color: C.rose, fontSize: 12.5, fontFamily: F.mono, lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
        <Btn ghost type="button" onClick={onCancel}>
          Cancel
        </Btn>
        <Btn primary type="submit" style={{ opacity: submitting || !valid ? 0.6 : 1 }}>
          {submitting ? "Saving…" : editing ? "Save changes" : "Add provider"}
        </Btn>
      </div>
    </form>
  );
}
